import React, { useState, useEffect, forwardRef } from 'react';
import Container from '../../Container/Container';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { get, post, downloadPostCsv, DELETE } from '../../ApiServices/ApiServices';
import Loader from '../Component/Loader';
import PurgeConfirmModal from './PurgeConfirmModal';
import './purgeOrders.css';

const ORDER_TYPES_LIST = [
    { label: 'Single', key: 'single' },
    { label: 'Prom', key: 'prom' },
    { label: 'Funeral', key: 'funeral' },
    { label: 'Wedding', key: 'wedding' },
    { label: 'Exchange', key: 'exchange' },
    { label: 'Order Type T', key: 'orderTypeT' }
];

const ALL_ORDER_TYPE_NAMES = ORDER_TYPES_LIST.map((o) => o.label);

export const PurgerOrders = () => {
    const [activeTab, setActiveTab] = useState(2);
    const [isConfirmed, setIsConfirmed] = useState(false);
    const [isRetentionConfirmed, setIsRetentionConfirmed] = useState(false);

    const [selectedDate, setSelectedDate] = useState(() => {
        const d = new Date();
        d.setFullYear(d.getFullYear() - 2);
        return d;
    });

    // Backend state
    const [defaultCutoffDate, setDefaultCutoffDate] = useState(null);
    const [oldestDate, setOldestDate] = useState(null);
    const [purgeHistory, setPurgeHistory] = useState([]);

    // Preview & loading states
    const [previewData, setPreviewData] = useState(null);
    const [isLoadingStats, setIsLoadingStats] = useState(false);
    const [isPreviewLoading, setIsPreviewLoading] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [isPurging, setIsPurging] = useState(false);

    // Modal state
    const [isModalOpen, setIsModalOpen] = useState(false);

    // 1. Fetch initial stats and history
    const fetchPurgeStats = async () => {
        try {
            setIsLoadingStats(true);
            const res = await get('orders/purge/stats');
            if (res?.status && res?.data) {
                if (res.data.defaultCutoffDate) {
                    setDefaultCutoffDate(res.data.defaultCutoffDate);
                }
                if (res.data.oldestDate) {
                    setOldestDate(res.data.oldestDate);
                }
                if (Array.isArray(res.data.history)) {
                    setPurgeHistory(res.data.history);
                }
            }
        } catch (error) {
            console.error('Failed to load purge config & stats:', error);
            toast.error(error?.message || 'Failed to fetch purge statistics');
        } finally {
            setIsLoadingStats(false);
        }
    };

    // 2. Fetch live preview counts
    const fetchPreviewCounts = async (dateToUse) => {
        if (!dateToUse) return;
        const formattedCutoff = moment(dateToUse).format('YYYY-MM-DD');
        try {
            setIsPreviewLoading(true);
            const res = await post('orders/purge/preview', {
                cutoffDate: formattedCutoff,
                orderTypes: ALL_ORDER_TYPE_NAMES
            });
            if (res?.status && res?.data) {
                setPreviewData(res.data);
            }
        } catch (error) {
            console.error('Failed to preview purge orders:', error);
            toast.error(error?.message || 'Failed to calculate eligible purge count');
        } finally {
            setIsPreviewLoading(false);
        }
    };

    useEffect(() => {
        fetchPurgeStats();
    }, []);

    // Re-fetch preview whenever selectedDate changes
    useEffect(() => {
        if (selectedDate) {
            fetchPreviewCounts(selectedDate);
        }
    }, [selectedDate]);

    // Handler to subtract years from today
    const handlePresetYears = (years) => {
        setActiveTab(years);
        const newDate = new Date();
        newDate.setFullYear(newDate.getFullYear() - years);
        setSelectedDate(newDate);
    };

    // When user manually picks a date from calendar
    const handleDateChange = (date) => {
        setSelectedDate(date);
        setActiveTab(null);
    };

    // Export CSV of eligible records
    const handleExport = async () => {
        if (!selectedDate) {
            toast.error('Please select a valid cutoff date');
            return;
        }
        const formattedCutoff = moment(selectedDate).format('YYYY-MM-DD');
        try {
            setIsExporting(true);
            const defaultFilename = `PurgeArchive_${moment(formattedCutoff).format('YYYYMMDD')}.csv`;
            await downloadPostCsv(
                'orders/purge/export',
                {
                    cutoffDate: formattedCutoff,
                    orderTypes: ALL_ORDER_TYPE_NAMES
                },
                defaultFilename
            );
            toast.success('Archive exported successfully');
        } catch (error) {
            console.error('Export error:', error);
            toast.error(error?.message || 'Failed to export purge records');
        } finally {
            setIsExporting(false);
        }
    };

    // Open confirmation modal
    const handleOpenReviewModal = () => {
        if (!isConfirmed || !isRetentionConfirmed) {
            toast.error('Please confirm both retention and irreversible purge acknowledgements');
            return;
        }
        if (!previewData || previewData.totalEligible === 0) {
            toast.info('There are 0 eligible orders to purge for this selection.');
            return;
        }
        setIsModalOpen(true);
    };

    // Execute the purge permanently
    const handleExecutePurge = async () => {
        const formattedCutoff = moment(selectedDate).format('YYYY-MM-DD');
        try {
            setIsPurging(true);
            const res = await post('orders/purge/execute', {
                cutoffDate: formattedCutoff,
                orderTypes: ALL_ORDER_TYPE_NAMES,
                confirmedPastRetention: isRetentionConfirmed,
                confirmedPermanent: isConfirmed
            });

            if (res?.status) {
                toast.success(res?.message || 'Orders purged successfully!');
                setIsModalOpen(false);
                setIsConfirmed(false);
                setIsRetentionConfirmed(false);

                // Refresh preview & history
                await fetchPurgeStats();
                await fetchPreviewCounts(selectedDate);
            } else {
                toast.error(res?.message || 'Failed to execute purge.');
            }
        } catch (error) {
            console.error('Purge execution failed:', error);
            toast.error(error?.message || 'Purge failed. Transaction rolled back.');
        } finally {
            setIsPurging(false);
        }
    };

    const deletePurgeHistory = (id) => {
        Swal.fire({
            title: 'Delete Purge Log?',
            text: 'Are you sure you want to delete this purge audit record? This cannot be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#e11d48',
            cancelButtonColor: '#6b7280',
            confirmButtonText: 'Yes, delete',
            cancelButtonText: 'Cancel',
            reverseButtons: true
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const response = await DELETE(`orders/deletePurgeOrderHistory/${id}`);

                    if (response?.status) {
                        toast.success(response?.message || 'Purge history deleted successfully!');
                        fetchPurgeStats();
                    } else {
                        toast.error(response?.message || 'Failed to delete purge history');
                    }
                } catch (error) {
                    toast.error(error?.message || 'Failed to delete purge history');
                }
            }
        });
    };
    // Custom Input for react-datepicker
    const CustomDateInput = forwardRef(({ value, onClick }, ref) => (
        <div className="flex-grow-1 position-relative" onClick={onClick} style={{ cursor: 'pointer' }}>
            <input
                ref={ref}
                type="text"
                value={value}
                readOnly
                style={{
                    width: '100%',
                    padding: '12px 42px 12px 16px',
                    borderRadius: '10px',
                    border: '1.5px solid var(--po-input-border)',
                    fontSize: '18px',
                    fontWeight: '700',
                    color: 'var(--po-input-text)',
                    outline: 'none',
                    background: 'var(--po-input-bg)',
                    cursor: 'pointer'
                }}
            />
            <span style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', color: 'var(--po-text-sub)', pointerEvents: 'none' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect width="18" height="18" x="3" y="4" rx="2" ry="2" />
                    <line x1="16" x2="16" y1="2" y2="6" />
                    <line x1="8" x2="8" y1="2" y2="6" />
                    <line x1="3" x2="21" y1="10" y2="10" />
                </svg>
            </span>
        </div>
    ));

    const totalEligibleCount = previewData?.totalEligible ?? 0;
    const breakdown = previewData?.breakdown || {};

    return (
        <>
            <Container>
                <div className="nk-content purge-orders-container">
                    <div className="d-flex flex-column">
                        <h4 className="text-bold" style={{ color: 'var(--po-text-heading)' }}>Purge orders</h4>
                        <p style={{ color: 'var(--po-text-sub)' }}>Permanently delete orders created before a selected date. This action cannot be undone.</p>
                    </div>

                    <div className="d-flex align-items-center mt-2" style={{ gap: '12px', border: "1px solid var(--po-alert-warn-border)", backgroundColor: 'var(--po-alert-warn-bg)', padding: '15px 17px', borderRadius: "11px" }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--po-alert-warn-icon)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-triangle-alert">
                            <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
                            <path d="M12 9v4" />
                            <path d="M12 17h.01" />
                        </svg>
                        <p className="mb-0" style={{ fontSize: "14px", color: 'var(--po-alert-warn-text)' }}>
                            Orders deleted by this process cannot be recovered. Ensure all required data has been archived or exported before proceeding.
                        </p>
                    </div>

                    {/* Stat Cards */}
                    <div className="po-stat-grid">
                        {/* Card 1: Default Cutoff */}
                        <div style={{
                            boxShadow: 'var(--po-card-shadow)',
                            padding: '24px',
                            borderRadius: "11px",
                            background: 'var(--po-card-bg)',
                            border: '1px solid var(--po-card-border)'
                        }}>
                            <p className="text-uppercase" style={{ fontSize: "11px", color: "var(--po-text-label)", fontWeight: '600' }}>
                                Default purge cutoff (2 Years)
                            </p>
                            <h5 style={{ fontSize: '20px', fontWeight: '700', marginTop: '10px', color: 'var(--po-text-heading)' }}>
                                {isLoadingStats ? '...' : (defaultCutoffDate ? moment(defaultCutoffDate).format('MMM DD, YYYY') : moment().subtract(2, 'years').format('MMM DD, YYYY'))}
                            </h5>
                        </div>

                        {/* Card 2: Eligible Orders */}
                        <div style={{
                            boxShadow: 'var(--po-card-shadow)',
                            padding: '24px',
                            borderRadius: "11px",
                            background: 'var(--po-card-bg)',
                            border: '1px solid var(--po-card-border)'
                        }}>
                            <p className="text-uppercase" style={{ fontSize: "11px", color: "var(--po-text-label)", fontWeight: '600' }}>
                                Orders eligible for purge
                            </p>
                            <h5 style={{ fontSize: '30px', fontWeight: '700', marginTop: '10px', color: '#e04138' }}>
                                {isPreviewLoading ? (
                                    <span style={{ fontSize: '20px', color: 'var(--po-text-sub)' }}>Calculating...</span>
                                ) : (
                                    totalEligibleCount.toLocaleString()
                                )}
                            </h5>
                        </div>

                        {/* Card 3: Date Range */}
                        <div style={{
                            boxShadow: 'var(--po-card-shadow)',
                            padding: '24px',
                            borderRadius: "11px",
                            background: 'var(--po-card-bg)',
                            border: '1px solid var(--po-card-border)'
                        }}>
                            <p className="text-uppercase" style={{ fontSize: "11px", color: "var(--po-text-label)", fontWeight: '600' }}>
                                {oldestDate ? `Earliest (${moment(oldestDate).format('MMM YYYY')}) to cutoff` : 'Date range'}
                            </p>
                            <h5 style={{ fontSize: '19px', fontWeight: '700', marginTop: '10px', color: 'var(--po-text-heading)' }}>
                                Up to {moment(selectedDate).format("MMM DD, YYYY")}
                            </h5>
                        </div>
                    </div>

                    {/* Purge Criteria Card */}
                    <div style={{
                        boxShadow: 'var(--po-card-shadow)',
                        borderRadius: "11px",
                        background: 'var(--po-card-bg)',
                        border: '1px solid var(--po-card-border)',
                        marginTop: '20px'
                    }}>
                        <div style={{ borderBottom: '1px solid var(--po-divider)', padding: '16px 24px' }}>
                            <h6 style={{ margin: 0, fontWeight: '700', fontSize: '13px', letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--po-text-heading)' }}>
                                Purge criteria
                            </h6>
                        </div>

                        <div style={{ padding: '24px' }}>
                            {/* Date Input Row */}
                            <div className="po-date-input-row">
                                <div className="po-date-label">
                                    Purge orders<br />created before
                                </div>
                                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                    <DatePicker
                                        selected={selectedDate}
                                        onChange={handleDateChange}
                                        dateFormat="MM-dd-yyyy"
                                        customInput={<CustomDateInput />}
                                        wrapperClassName="w-100"
                                        maxDate={new Date()}
                                    />
                                </div>
                                <div className="po-date-badge-wrapper">
                                    <div style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        padding: '10px 18px',
                                        borderRadius: '10px',
                                        border: '1.5px solid var(--po-date-badge-border)',
                                        backgroundColor: 'var(--po-date-badge-bg)',
                                        color: 'var(--po-date-badge-text)',
                                        fontWeight: '600',
                                        fontSize: '14px',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <rect width="18" height="18" x="3" y="4" rx="2" ry="2" />
                                            <line x1="16" x2="16" y1="2" y2="6" />
                                            <line x1="8" x2="8" y1="2" y2="6" />
                                            <line x1="3" x2="21" y1="10" y2="10" />
                                        </svg>
                                        {selectedDate ? moment(selectedDate).format('MM/DD/YYYY') : '--/--/----'}
                                    </div>
                                </div>
                            </div>

                            {/* Preset Buttons */}
                            <div className="po-preset-container">
                                {[
                                    { label: '1 year', years: 1 },
                                    { label: '2 years', years: 2 },
                                    { label: '3 years', years: 3 },
                                    { label: '5 years', years: 5 }
                                ].map((preset) => {
                                    const isActive = activeTab === preset.years;
                                    return (
                                        <button
                                            key={preset.label}
                                            type="button"
                                            className="po-preset-btn"
                                            onClick={() => handlePresetYears(preset.years)}
                                            style={{
                                                border: isActive ? '1.5px solid var(--po-btn-preset-active-border)' : '1.5px solid var(--po-btn-preset-border)',
                                                background: isActive ? 'var(--po-btn-preset-active-bg)' : 'var(--po-btn-preset-bg)',
                                                borderRadius: '20px',
                                                padding: '5px 18px',
                                                fontSize: '13px',
                                                fontWeight: '600',
                                                color: isActive ? 'var(--po-btn-preset-active-color)' : 'var(--po-btn-preset-color)',
                                                cursor: 'pointer',
                                                transition: 'all 0.15s ease'
                                            }}
                                        >
                                            {preset.label}
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Order Types Section */}
                            <div>
                                <p style={{
                                    fontSize: '12px',
                                    fontWeight: '700',
                                    color: 'var(--po-text-sub)',
                                    letterSpacing: '0.04em',
                                    textTransform: 'uppercase',
                                    marginBottom: '12px'
                                }}>
                                    Order types included
                                </p>

                                <div className="po-order-types-grid">
                                    {ORDER_TYPES_LIST.map((item, idx) => {
                                        const count = breakdown[item.key] ?? 0;
                                        return (
                                            <div
                                                key={idx}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    padding: '14px 18px',
                                                    borderRadius: '10px',
                                                    border: '1px solid var(--po-badge-border)',
                                                    background: 'var(--po-badge-bg)',
                                                    margin: 0
                                                }}
                                            >
                                                <span style={{ fontSize: '15px', fontWeight: '700', color: 'var(--po-text-heading)' }}>
                                                    {item.label}
                                                </span>
                                                <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--po-badge-count)' }}>
                                                    {isPreviewLoading ? '...' : count.toLocaleString()}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Note Section */}
                            <div style={{
                                background: 'var(--po-note-bg)',
                                border: '1px solid var(--po-note-border)',
                                borderRadius: '10px',
                                padding: '16px 20px'
                            }}>
                                <div style={{ fontSize: '12px', fontWeight: '700', color: 'var(--po-note-title)', letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: '4px' }}>
                                    Note
                                </div>
                                <div style={{ fontSize: '14px', color: 'var(--po-note-text)', fontWeight: '500' }}>
                                    Matching delinquent records ({breakdown?.delinquent ?? 0} found) will also be deleted for the selected date range.
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Permanent Deletion Card */}
                    <div style={{
                        border: "1px solid var(--po-del-card-border)",
                        borderRadius: '11px',
                        marginTop: '20px',
                        background: 'var(--po-card-bg)',
                        overflow: 'hidden'
                    }}>
                        {/* Header */}
                        <div style={{
                            borderBottom: "1px solid var(--po-del-header-border)",
                            background: 'var(--po-del-header-bg)',
                            padding: '14px 24px',
                            color: 'var(--po-del-header-text)',
                            fontSize: '12px',
                            fontWeight: '700',
                            letterSpacing: '0.05em',
                            textTransform: 'uppercase'
                        }}>
                            Permanent deletion
                        </div>

                        <div style={{ padding: '24px' }}>
                            {/* Alert Headline */}
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '8px' }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--po-del-header-text)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ marginTop: '2px', flexShrink: 0 }}>
                                    <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
                                    <path d="M12 9v4" />
                                    <path d="M12 17h.01" />
                                </svg>
                                <div>
                                    <h6 style={{ fontSize: '16px', fontWeight: '700', color: 'var(--po-text-heading)', margin: '0 0 6px 0' }}>
                                        This cannot be undone.
                                    </h6>
                                    <p style={{ fontSize: '14px', color: 'var(--po-del-desc)', margin: 0 }}>
                                        Deletes <strong style={{ fontWeight: '700', color: 'var(--po-text-heading)' }}>{totalEligibleCount.toLocaleString()} orders</strong> of type <strong style={{ fontWeight: '700', color: 'var(--po-text-heading)' }}>all types</strong> created before <strong style={{ fontWeight: '700', color: 'var(--po-text-heading)' }}>{moment(selectedDate).format("MMMM DD, YYYY")}</strong>.
                                    </p>
                                </div>
                            </div>

                            {/* Checkbox 1 */}
                            <div
                                onClick={() => setIsRetentionConfirmed(!isRetentionConfirmed)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    marginTop: '20px',
                                    cursor: 'pointer'
                                }}
                            >
                                <div
                                    style={{
                                        width: '18px',
                                        height: '18px',
                                        borderRadius: '4px',
                                        backgroundColor: isRetentionConfirmed ? '#be123c' : 'var(--po-input-bg)',
                                        border: isRetentionConfirmed ? '1.5px solid #be123c' : '1.5px solid var(--po-input-border)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        transition: 'all 0.15s ease',
                                        flexShrink: 0
                                    }}
                                >
                                    {isRetentionConfirmed && (
                                        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="20 6 9 17 4 12" />
                                        </svg>
                                    )}
                                </div>
                                <span style={{ fontSize: '14px', color: 'var(--po-del-desc)', userSelect: 'none' }}>
                                    I confirm these records are <strong style={{ fontWeight: '700', color: 'var(--po-text-heading)' }}>past our retention requirement</strong> for tax and accounting.
                                </span>
                            </div>

                            {/* Checkbox 2 */}
                            <div
                                onClick={() => setIsConfirmed(!isConfirmed)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    marginTop: '14px',
                                    cursor: 'pointer'
                                }}
                            >
                                <div
                                    style={{
                                        width: '18px',
                                        height: '18px',
                                        borderRadius: '4px',
                                        backgroundColor: isConfirmed ? '#be123c' : 'var(--po-input-bg)',
                                        border: isConfirmed ? '1.5px solid #be123c' : '1.5px solid var(--po-input-border)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        transition: 'all 0.15s ease',
                                        flexShrink: 0
                                    }}
                                >
                                    {isConfirmed && (
                                        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="20 6 9 17 4 12" />
                                        </svg>
                                    )}
                                </div>
                                <span style={{ fontSize: '14px', color: 'var(--po-del-desc)', userSelect: 'none' }}>
                                    I understand this purge is <strong style={{ fontWeight: '700', color: 'var(--po-text-heading)' }}>permanent and not reversible.</strong>
                                </span>
                            </div>

                            {/* Action Buttons Row */}
                            <div className="po-action-buttons-row">
                                {/* <button
                                    type="button"
                                    onClick={handleExport}
                                    disabled={isExporting}
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        padding: '10px 20px',
                                        borderRadius: '8px',
                                        border: '1.5px solid var(--po-btn-export-border)',
                                        background: 'var(--po-btn-export-bg)',
                                        fontSize: '14px',
                                        fontWeight: '700',
                                        color: 'var(--po-btn-export-color)',
                                        cursor: isExporting ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                        <polyline points="7 10 12 15 17 10" />
                                        <line x1="12" x2="12" y1="15" y2="3" />
                                    </svg>
                                    {isExporting ? 'Exporting Archive...' : 'Export list first'}
                                </button> */}

                                <button
                                    type="button"
                                    onClick={handleOpenReviewModal}
                                    disabled={!isConfirmed || !isRetentionConfirmed || totalEligibleCount === 0}
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        padding: '10px 22px',
                                        borderRadius: '8px',
                                        border: 'none',
                                        background: (isConfirmed && isRetentionConfirmed && totalEligibleCount > 0) ? '#c42b21' : 'var(--po-card-border)',
                                        fontSize: '14px',
                                        fontWeight: '700',
                                        color: (isConfirmed && isRetentionConfirmed && totalEligibleCount > 0) ? '#ffffff' : 'var(--po-text-sub)',
                                        cursor: (isConfirmed && isRetentionConfirmed && totalEligibleCount > 0) ? 'pointer' : 'not-allowed',
                                        transition: 'all 0.15s ease'
                                    }}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M3 6h18" />
                                        <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                                        <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                                    </svg>
                                    Review & Purge
                                </button>

                                {(!isConfirmed || !isRetentionConfirmed) ? (
                                    <span style={{ fontSize: '13px', color: 'var(--po-text-sub)', fontWeight: '500' }}>
                                        Confirm both items to continue.
                                    </span>
                                ) : (
                                    <span style={{ fontSize: '13px', color: 'var(--po-text-sub)', fontWeight: '500' }}>
                                        You will confirm once more before anything is deleted.
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Past Purge History Section */}
                    <div style={{
                        boxShadow: 'var(--po-card-shadow)',
                        borderRadius: "11px",
                        background: 'var(--po-card-bg)',
                        border: '1px solid var(--po-card-border)',
                        marginTop: '24px',
                        overflow: 'hidden'
                    }}>
                        <div style={{ borderBottom: '1px solid var(--po-divider)', padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h6 style={{ margin: 0, fontWeight: '700', fontSize: '13px', letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--po-text-heading)' }}>
                                Past Purge History & Audit Log
                            </h6>
                            <button
                                type="button"
                                onClick={fetchPurgeStats}
                                style={{
                                    border: 'none',
                                    background: 'none',
                                    color: '#3874ff',
                                    fontSize: '13px',
                                    cursor: 'pointer',
                                    fontWeight: '600'
                                }}
                            >
                                Refresh history
                            </button>
                        </div>

                        <div style={{ overflowX: 'auto' }}>
                            {isLoadingStats ? (
                                <div style={{ padding: '24px' }}>
                                    <Loader />
                                </div>
                            ) : purgeHistory.length === 0 ? (
                                <div style={{ padding: '30px 24px', textAlign: 'center', color: 'var(--po-text-sub)', fontSize: '14px' }}>
                                    No past purges recorded yet.
                                </div>
                            ) : (
                                <table className="purge-history-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ background: 'var(--po-badge-bg)' }}>
                                            <th style={{ textAlign: 'left' }}>Purge Date</th>
                                            <th style={{ textAlign: 'left' }}>Executed By</th>
                                            <th style={{ textAlign: 'left' }}>Cutoff Date</th>
                                            <th style={{ textAlign: 'center' }}>Orders Purged</th>
                                            <th style={{ textAlign: 'left' }}>Breakdown</th>
                                            <th style={{ textAlign: 'left' }}>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {purgeHistory.map((item) => {
                                            const breakdownObj = typeof item.breakdown_counts === 'string'
                                                ? JSON.parse(item.breakdown_counts || '{}')
                                                : (item.breakdown_counts || {});
                                            return (
                                                <tr key={item.id}>
                                                    <td>
                                                        <strong>{moment(item.createdAt).format('MMM DD, YYYY')}</strong>
                                                        <br />
                                                        <span style={{ fontSize: '11px', color: 'var(--po-text-sub)' }}>
                                                            {moment(item.createdAt).format('hh:mm A')}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {item.user ? (
                                                            <>
                                                                <span style={{ fontWeight: '600' }}>{item.user.firstName} {item.user.lastName}</span>
                                                                <br />
                                                                <span style={{ fontSize: '11px', color: 'var(--po-text-sub)' }}>{item.user.email}</span>
                                                            </>
                                                        ) : (
                                                            <span style={{ color: 'var(--po-text-sub)' }}>System / Admin #{item.user_id}</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <span style={{
                                                            display: 'inline-block',
                                                            padding: '3px 8px',
                                                            borderRadius: '6px',
                                                            backgroundColor: 'var(--po-date-badge-bg)',
                                                            color: 'var(--po-date-badge-text)',
                                                            fontSize: '12px',
                                                            fontWeight: '600'
                                                        }}>
                                                            {moment(item.cutoff_date).format('MMM DD, YYYY')}
                                                        </span>
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        <span style={{
                                                            fontSize: '15px',
                                                            fontWeight: '700',
                                                            color: '#e04138'
                                                        }}>
                                                            {item.total_orders_purged?.toLocaleString() || 0}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div style={{ fontSize: '11px', color: 'var(--po-text-sub)', lineHeight: 1.4 }}>
                                                            {Object.entries(breakdownObj)
                                                                .filter(([_, count]) => count > 0)
                                                                .map(([k, count]) => `${k}: ${count}`)
                                                                .join(' | ') || 'N/A'}
                                                        </div>
                                                    </td>
                                                    <td style={{ fontSize: '12px' }}>
                                                        <button
                                                            type="button"
                                                            className="po-btn-delete-action"
                                                            title="Delete purge record"
                                                            onClick={() => deletePurgeHistory(item.id)}
                                                        >
                                                            <svg
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                width="14"
                                                                height="14"
                                                                viewBox="0 0 24 24"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                            >
                                                                <path d="M3 6h18" />
                                                                <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                                                                <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                                                                <line x1="10" y1="11" x2="10" y2="17" />
                                                                <line x1="14" y1="11" x2="14" y2="17" />
                                                            </svg>
                                                            <span>Delete</span>
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>

                </div>
            </Container>

            {/* Final Confirmation Modal */}
            <PurgeConfirmModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onConfirm={handleExecutePurge}
                cutoffDate={selectedDate}
                selectedOrderTypes={ALL_ORDER_TYPE_NAMES}
                previewData={previewData}
                isPurging={isPurging}
            />
        </>
    );
};

export default PurgerOrders;